import { useEffect, useRef } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { BaseLogo } from "../core/models/entities/BaseLogo";
import { actAsCacheKey } from "../contexts/actAsStore";
import { useApiClient } from "../data/clients/ApiClient";
import { StorageFreshnessResponseDto } from "../data/models/responses/VerifyStorageResponseDto";
import { enviroment } from "../enviroment";
import {
  logoIdsSignature,
  patchStorageInQueryCache,
  requestedStore,
  selectStaleLogoIds,
  STORAGE_FRESHNESS_DEBOUNCE_MS,
  STORAGE_FRESHNESS_MAX_AGE_HOURS,
  STORAGE_FRESHNESS_MAX_IDS,
  STORAGE_FRESHNESS_REQUEST_TTL_MS,
  STORAGE_FRESHNESS_TIMEOUT_MS,
} from "../utils/functions/storageFreshness";

// The API's threshold (StorageFreshnessMaxAgeHours) once an answer carried
// it; until then the FE default. Module-level so the list and the detail
// agree for the rest of the session.
let maxAgeHours = STORAGE_FRESHNESS_MAX_AGE_HOURS;

// The API answered enabled=false (its flag StorageFreshnessOnViewEnabled is
// off, or was just switched off as a kill switch): no call at all until this
// instant - one probe per dedupe window instead of one per page view, since
// the API still reads its rows before it notices its flag.
let apiDisabledUntilMs = 0;

// Whoever shows a "refresh stock now" button listens here: the instant an
// answer says enabled=false every button must read "unavailable" at once -
// not at the next minute tick, with a click in between that silently does
// nothing.
const apiDisabledListeners = new Set<() => void>();

type Refresh = (
  orgId: number,
  logoIds: number[],
  signal?: AbortSignal
) => Promise<StorageFreshnessResponseDto>;

interface Snapshot {
  logos: readonly BaseLogo[] | undefined;
  refresh: Refresh;
  queryClient: QueryClient;
}

// Every mounted hook instance with something on screen for an organization.
// The list and the detail on the same page (in whatever order they mounted
// and settled) feed ONE debounced timer per organization, which fires with
// the union of their logos - a deep-linked detail never costs a call of its
// own when the list carries the same logo.
interface Contributor {
  orgId: number;
  current: () => Snapshot;
}

const contributors = new Set<Contributor>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

export function getStorageFreshnessMaxAgeHours(): number {
  return maxAgeHours;
}

/** Until when the API is known to answer enabled=false (0 = not known). */
export function getApiDisabledUntilMs(): number {
  return apiDisabledUntilMs;
}

export function subscribeApiDisabled(listener: () => void): () => void {
  apiDisabledListeners.add(listener);
  return () => {
    apiDisabledListeners.delete(listener);
  };
}

export function resetStorageFreshnessForTests(): void {
  maxAgeHours = STORAGE_FRESHNESS_MAX_AGE_HOURS;
  apiDisabledUntilMs = 0;
  apiDisabledListeners.clear();
  contributors.clear();
  timers.forEach((timer) => clearTimeout(timer));
  timers.clear();
}

/**
 * Lands ONE storage-freshness answer, whoever asked (the on-view call or a
 * "refresh stock now" click): an API answering enabled=false (its flag off)
 * silences every call for the next 5 minutes and patches nothing; otherwise
 * the API's max age is remembered and the verified rows are patched into the
 * react-query entries of `scope` (the act-as scope the request was made in).
 * Returns whether the API was enabled and how many cache entries changed.
 */
export function landFreshnessResponse(
  response: StorageFreshnessResponseDto | undefined,
  queryClient: QueryClient,
  scope: number
): { enabled: boolean; patched: number } {
  if (!response?.enabled) {
    apiDisabledUntilMs = Date.now() + STORAGE_FRESHNESS_REQUEST_TTL_MS;
    apiDisabledListeners.forEach((listener) => listener());
    return { enabled: false, patched: 0 };
  }
  if (
    typeof response.maxAgeHours === "number" &&
    response.maxAgeHours > 0 &&
    response.maxAgeHours !== maxAgeHours
  ) {
    maxAgeHours = response.maxAgeHours;
  }
  const rows = (response.rows ?? []).filter((row) => row.verified);
  const patched =
    rows.length > 0 ? patchStorageInQueryCache(queryClient, rows, scope) : 0;
  return { enabled: true, patched };
}

function schedule(orgId: number, delayMs: number): void {
  const pending = timers.get(orgId);
  if (pending != null) clearTimeout(pending);
  timers.set(
    orgId,
    setTimeout(() => {
      timers.delete(orgId);
      fire(orgId);
    }, delayMs)
  );
}

function cancelWhenIdle(orgId: number): void {
  let contributing = false;
  contributors.forEach((c) => {
    if (c.orgId === orgId) contributing = true;
  });
  if (contributing) return;
  const pending = timers.get(orgId);
  if (pending == null) return;
  clearTimeout(pending);
  timers.delete(orgId);
}

// ONE call for the organization: the stale, not-yet-asked logos across every
// contributor on screen right now (read at THIS moment - a cache patch or a
// background refetch may have replaced what a component held when it
// scheduled), capped at the API's batch size. The answer is patched into the
// react-query entries of the act-as scope it was fetched for, and a page
// wider than the cap gets its next batch only once this one has answered -
// never two in the air.
function fire(orgId: number): void {
  const now = Date.now();
  if (now < apiDisabledUntilMs) return;
  const union: BaseLogo[] = [];
  let snapshot: Snapshot | undefined;
  contributors.forEach((c) => {
    if (c.orgId !== orgId) return;
    const current = c.current();
    if (!snapshot) snapshot = current;
    if (current.logos) union.push(...current.logos);
  });
  if (!snapshot) return;
  const stale = selectStaleLogoIds(
    orgId,
    union,
    now,
    maxAgeHours,
    requestedStore
  ).slice(0, STORAGE_FRESHNESS_MAX_IDS);
  if (stale.length === 0) return;
  // Marked BEFORE the call: whoever computes a stale set meanwhile (a second
  // page, StrictMode's second pass) sees them as asked for.
  requestedStore.mark(orgId, stale, now);

  // The scope the rows belong to is the one of the request, read now: an
  // admin may switch act-as before the answer lands.
  const scope = actAsCacheKey();
  const { refresh, queryClient } = snapshot;
  const controller = new AbortController();
  const abortTimer = setTimeout(
    () => controller.abort(),
    STORAGE_FRESHNESS_TIMEOUT_MS
  );
  // No "cancelled" guard on purpose: the answer belongs in the cache even
  // when every component is gone by then (list -> detail -> list).
  refresh(orgId, stale, controller.signal)
    .finally(() => clearTimeout(abortTimer))
    .then((response) => {
      if (!landFreshnessResponse(response, queryClient, scope).enabled) return;
      // Next batch, if the page still shows stale logos nobody asked for
      // (100 rows/page, all legacy): fires only when a contributor is still
      // mounted, and finds nothing once the page is covered.
      schedule(orgId, 0);
    })
    .catch(() => undefined);
}

/**
 * Stock freshness on view (flag storageFreshnessEnabled; Nichlas 7/9: "the
 * count on the front page must be correct"). Given the logos a page shows,
 * asks the API - ONCE per page view, debounced 1 s, at most once per (org,
 * logo) per 5 minutes, never while the page is still settling - to refresh
 * the storage rows that are stale (missing, never stamped or older than the
 * max age) and patches the answer into the react-query cache of the current
 * act-as scope, so the rows on screen switch to the fresh count and "Stock
 * updated just now" a moment later. Never blocking: the page renders from
 * the cached figures, the patch lands afterwards. Timeout, abort, transport
 * error or "not verified" rows are silence; an API answering enabled=false
 * (its flag off) silences every call for the next 5 minutes. Flag off, orgId
 * 0 or `settled` false = no call, no state, no DOM.
 *
 * Every instance on the same page (the list and a detail popup) feeds ONE
 * timer per organization, so they cost one call with the union of their
 * logos - also for a deep-linked detail, which waits for the list to settle.
 * Call budget: 0 calls when nothing on screen is stale; otherwise one call of
 * up to 50 ids. A page wider than the cap (100 rows/page, all stale on the
 * first day) gets its remaining ids in a follow-up call only after the first
 * has answered - never two in the air; "the count must be correct" outranks
 * "one call per page view" there, and after that first day the rows carry a
 * stamp and cost nothing for 24 h.
 *
 * @param orgId  The organization whose storage rows the page shows (see
 *               resolveStorageFreshnessOrgId); 0/undefined = idle.
 * @param logos  The logos on screen; only their id + storageUpdatedAtUtc
 *               matter. Array identity is irrelevant (keyed by the ids).
 * @param settled  False while the page shows previous data / is fetching -
 *               the stale set would otherwise be picked from the wrong page.
 *               A detail opened on the logo list passes the LIST's settled
 *               state, so it joins the list's call instead of racing it.
 */
export function useStorageFreshness(
  orgId: number | undefined,
  logos: readonly BaseLogo[] | undefined,
  settled: boolean
): void {
  const queryClient = useQueryClient();
  const { refreshStorageFreshness } = useApiClient();

  // The timer fires later: it reads the logos and the client of THAT moment.
  const latestRef = useRef<Snapshot>({
    logos,
    refresh: refreshStorageFreshness,
    queryClient,
  });
  latestRef.current = { logos, refresh: refreshStorageFreshness, queryClient };

  const enabled =
    enviroment.storageFreshnessEnabled && !!orgId && orgId > 0 && settled;
  const idsSignature = enabled ? logoIdsSignature(logos) : "";

  useEffect(() => {
    if (!enabled || !idsSignature || !orgId) return;
    const contributor: Contributor = {
      orgId,
      current: () => latestRef.current,
    };
    contributors.add(contributor);
    schedule(orgId, STORAGE_FRESHNESS_DEBOUNCE_MS);
    return () => {
      contributors.delete(contributor);
      cancelWhenIdle(orgId);
    };
  }, [enabled, orgId, idsSignature]);
}
